import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { TrendingUp, Play } from 'lucide-react';
import FrequencySlider from './FrequencySlider';

interface ProgressiveSessionProps {
  onCreateSession: (
    startFreq: number, 
    endFreq: number, 
    duration: number, 
    transitionTime: number
  ) => void;
}

const ProgressiveSessions: React.FC<ProgressiveSessionProps> = ({ onCreateSession }) => {
  const [startFrequency, setStartFrequency] = useState(10);
  const [endFrequency, setEndFrequency] = useState(4);
  const [duration, setDuration] = useState(20);
  const [transitionTime, setTransitionTime] = useState(5);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    setTimeout(() => {
      onCreateSession(startFrequency, endFrequency, duration, transitionTime);
      setIsSubmitting(false);
    }, 300);
  };
  
  return (
    <div className="glass rounded-2xl p-6 animate-fade-in border-2 border-primary/20">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp size={18} className="text-primary" />
        <h3 className="text-lg font-medium">Progressive Session</h3>
      </div>
      
      <p className="text-sm text-muted-foreground mb-4">
        Create a session that gradually shifts from one frequency to another,
        perfect for guiding your brain state through different stages.
      </p>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <h4 className="text-sm font-medium">Starting Frequency</h4>
          <FrequencySlider
            value={startFrequency}
            onChange={setStartFrequency}
            label="Starting Frequency"
          />
        </div>
        
        <div className="space-y-4">
          <h4 className="text-sm font-medium">Target Frequency</h4>
          <FrequencySlider
            value={endFrequency}
            onChange={setEndFrequency}
            label="Target Frequency"
          />
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Session Duration</span>
            <span className="text-sm font-mono">{duration} min</span>
          </div>
          
          <Slider
            value={[duration]}
            min={5}
            max={60}
            step={5}
            onValueChange={(values) => setDuration(values[0])}
          />
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Transition Time</span>
            <span className="text-sm font-mono">{transitionTime} min</span>
          </div>
          
          <Slider
            value={[transitionTime]}
            min={1}
            max={20}
            step={1}
            onValueChange={(values) => setTransitionTime(values[0])}
          />
          
          <p className="text-xs text-muted-foreground">
            How long it takes to transition from start to target frequency
          </p>
        </div>
        
        <Button 
          type="submit" 
          className="w-full"
          variant="default"
          disabled={isSubmitting}
        >
          <Play className="h-4 w-4 mr-2" />
          {isSubmitting ? 'Starting...' : 'Start Progressive Session'}
        </Button>
      </form>
    </div>
  );
};

export default ProgressiveSessions;
